import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ShareModel } from "common247";

/**
 * State shape for the shares slice.
 */
export interface IShareState {
  shares: ShareModel[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: IShareState = {
  shares: [],
  loading: false,
  error: null,
  lastFetched: null,
};

export const sharesSlice = createSlice({
  name: "shares",
  initialState,
  reducers: {
    /**
     * Sets the full list of shares and updates lastFetched timestamp.
     */
    setShares: (state, action: PayloadAction<ShareModel[]>) => {
      state.shares = action.payload;
      state.loading = false;
      state.error = null;
      state.lastFetched = Date.now();
    },

    /**
     * Upserts a single share in the store.
     */
    addOrUpdateShare: (state, action: PayloadAction<ShareModel>) => {
      const incoming = action.payload;
      const index = state.shares.findIndex((s) => s.id === incoming.id);
      if (index !== -1) {
        state.shares[index] = { ...state.shares[index], ...incoming };
      } else {
        state.shares.unshift(incoming);
      }
    },

    /**
     * Removes a share by ID.
     */
    removeShare: (state, action: PayloadAction<string>) => {
      state.shares = state.shares.filter((s) => s.id !== action.payload);
    },

    /**
     * Sets loading indicator.
     */
    setSharesLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    /**
     * Sets error message.
     */
    setSharesError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },

    /**
     * Invalidates cache to prompt re-fetch.
     */
    invalidateShares: (state) => {
      state.lastFetched = null;
    },
  },
});

export const {
  setShares,
  addOrUpdateShare,
  removeShare,
  setSharesLoading,
  setSharesError,
  invalidateShares,
} = sharesSlice.actions;

export default sharesSlice.reducer;
